// src/components/MatchMakingPage.js
import axios from 'axios';
import '../utils/axiosConfig'; // runs the 401/403 logout logic globally
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "../Style/MatchForm.css";
import "../Style/Responsive.css";
import { FaEnvelope,FaSpinner } from 'react-icons/fa';


const financialsMessage = `Do you have up-to-date financial records (books of account) for your business?`;
const eligibilityMessage = `Before matching, you will undergo an assessment based on the following eligibility criteria:
1. Registration - Be a registered company in either country.
2. Years of operation - years of operation.
3. Revenue - Provide your annual revenue.
4. Team capacity - Minimum 5 full-time staff plus founder; strength of business & technical leads.
5. Ability to scale - Evidence of customer acquisition, market share expansion, and a growth strategy for funding use.
6. Sector - Must be in tech, transport, construction, health & education, hotels & food, agriculture, trade, or energy.
7. Financial records - Two years of consistent financial statements.
8. Fundraising need - Demonstrable financing gap > specified threshold; amount and type required.
9. Pitching language - All pitches conducted in English.

Do you wish to proceed with matching?`;


const MatchMakingPage = () => {
 

  const [matchedResults,   setMatchedResults]   = useState([]);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showEnquiryModal,   setShowEnquiryModal]   = useState(false);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [rerunCount,       setRerunCount]       = useState(0);
  const [rerunError,       setRerunError]       = useState('');
   const [matchMessage,     setMatchMessage]     = useState('');
const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [investeeProfile, setInvesteeProfile] = useState(null);
  const [matchStatus, setMatchStatus] = useState(''); // '', 'matches_found', or 'no_matches'
  const navigate = useNavigate();

  // Check if the investee profile has the key fields
  const hasCompleteProfile = investeeProfile &&
    investeeProfile.business_name &&
    investeeProfile.business_type &&
    investeeProfile.business_stage &&
    investeeProfile.ticket_size &&
    investeeProfile.sector_type;

  // Maximum matches limit (2)
  const isMatchLimitReached = matchedResults.length >= 2;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  
      // Fetch the current user's investee profile from the backend
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/profile/`, {
      headers: { Authorization: `Token ${token}` },
    })
    .then(response => {
      const profile = response.data;
      // For frontend convenience, map "capital_needed" to "ticket_size"
      profile.ticket_size = profile.capital_needed;
      setInvesteeProfile(profile);
    })
    .catch(err => {
      console.error("Error fetching investee profile:", err);
      setInvesteeProfile(null);
    });
  }, [navigate]);

  // Redirect to the profile page if profile is incomplete
  const redirectToProfile = () => {
    navigate('/profile');
  };

  // Handle the matching request
  const handleGetMatched = () => {
    if (!hasCompleteProfile) {
      alert("Please complete your profile with all required details before matching.");
      redirectToProfile();
      return;
    }
    if (isMatchLimitReached) {
      alert("You have reached the maximum number of matches (2).");
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      setIsLoadingMatches(true); 
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/match_investors/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then(response => {
        // Expect the API to return an object with "matching_investors"
        const newMatches = response.data.matching_investors;
       if (newMatches && newMatches.length > 0) {
  setMatchedResults(newMatches.slice(0, 2));
  setMatchStatus('matches_found');
  setMatchMessage(
    "🎉 Great news! We’ve found potential investors for you and your matches are now under review for final approvals. " +
    "Hang tight—our team is checking your eligibility and will reach out with next steps."
  );
} else {
  setMatchedResults([]);
  setMatchStatus('no_matches');
  setMatchMessage('');  // clear any prior flashes
}
      })
      .catch(err => {
        console.error("Error fetching matched investors:", err);
        setMatchedResults([]);
        setMatchStatus('no_matches');
      })
      .finally(() => setIsLoadingMatches(false));
    }
  };
  


  // Financials → Yes → Eligibility
  const handleFinancialYes = () => {
    setShowFinancialModal(false);
    setShowEligibilityModal(true);
  };
  // Financials → No → Enquiry
  const handleFinancialNo = () => {
    setShowFinancialModal(false);
    setShowEnquiryModal(true);
  };

  // Eligibility confirm
  const confirmEligibility = () => {
    setShowEligibilityModal(false);
  };
  const cancelEligibility = () => setShowEligibilityModal(false);

  // Re-run logic (still behind eligibility)
  const handleRerunMatch = () => {
    if (matchedResults.length < 2) {
      setRerunError("You need at least two existing matches before re-running.");
      return;
    }
    if (rerunCount >= 2) {
      setRerunError("You’ve reached your two re-run limit.");
      return;
    }
    setRerunError('');
    setShowFinancialModal(true);
    setRerunCount(c => c + 1);
  };

  // Build mailto link for enquiry
  const openEnquiryEmail = e => {
    e.preventDefault();
    const email = 'go-fmafricaprivateen@kpmg.com';
    const cc      = 'privateenterprise@kpmg.co.ke';
    const subject = 'Assistance Needed: Financial Records for Matching';
    const body = `Hello KPMG Team,

I hope you’re well. I need assistance obtaining or organizing my company’s financial records so I can proceed with the investor matching process. Please let me know what documentation you require and the next steps.

Company Email: [your company email]

Thank you for your support.

Kind regards,
[Your Name]
[Your Company Name]`;

// build one params string for reuse
const params = [
  `subject=${encodeURIComponent(subject)}`,
  `cc=${encodeURIComponent(cc)}`,
  `body=${encodeURIComponent(body)}`
].join('&');

// mailto fallback
const mailto = `mailto:${encodeURIComponent(email)}?${params}`;

if (navigator.userAgent.includes('Outlook')) {
  window.open(
    `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(email)}&${params}`,
    '_blank'
  );
} else if (window.location.hostname.includes('mail.google')) {
  window.open(
    `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&${params}`,
    '_blank'
  );
} else {
  window.location.href = mailto;
}
  };

  // While profile is loading, display a loading message
  if (!investeeProfile) {
    return (
      <div className="matchmaking-container">
        <h1>Matchmaking</h1>
        <p>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="matchmaking-container">
       <h1>Matchmaking Dashboard</h1>
{/* ——— Matching Page Intro ——— */}
<p className="intro-text">
  Welcome to your personalized Investor Matchmaking Hub!  
  Here at KPMG Private Enterprise, we connect you with vetted investors who share your vision and growth ambitions, whether you’re at seed stage or scaling to Series A and beyond.  
  Complete your profile, confirm your financial records, review our eligibility criteria, then hit “Get Matched” to discover the perfect funding partner for your journey.  
  Let’s unlock your next chapter of growth together!
</p>
      <div className="profile-summary">
        <h3>Required Information for Matching</h3>
        <div className="matching-fields-container"> 
          <p><b>Business Name:</b> {investeeProfile.business_name || <span id="not-provided">Not Provided</span>}</p>
        </div>
        <div className="matching-fields-container"> 
          <p><b>Business Stage:</b> {investeeProfile.business_stage || <span id="not-provided">Not Provided</span>}</p>
        </div>
        <div className="matching-fields-container"> 
          <p><b>Business Type:</b> {investeeProfile.business_type || <span id="not-provided">Not Provided</span>}</p>
        </div>
        <div className="matching-fields-container"> 
          <p>
            <b>Capital Needed (USD):</b> 
            {investeeProfile.ticket_size ? `$${investeeProfile.ticket_size}` : <span id="not-provided">Not Provided</span>}
          </p>
        </div>
        <div className="matching-fields-container"> 
          <p><b>Sector Type:</b> {investeeProfile.sector_type || <span id="not-provided">Not Provided</span>}</p>
        </div>

        {!hasCompleteProfile && (
          <div className="redirect-profile">
            <p>Please complete your profile with the required details above for you to get matched.</p>
            <button onClick={redirectToProfile}>Go to Profile</button>
          </div>
        )}
      </div>

      {/* ——— Success Message ——— */}
{matchMessage && (
  <div className="match-success-message">
    {matchMessage}
  </div>
)}

      {/* only show “Get Matched” if no matches yet */}
      <div className="match-btn-container">
    <button 
      onClick={handleGetMatched}
      disabled={!hasCompleteProfile || isMatchLimitReached || isLoadingMatches}
    >
      {isMatchLimitReached 
        ? "Maximum Matches Reached" 
        : isLoadingMatches 
          ? "Matching…"          // optional: change button text
          : "Get Matched"}
    </button>

    {isLoadingMatches && (
      <span className="loading-text">
        <FaSpinner className="spin-icon"/> Matching...
      </span>
    )}
  </div>


{/* ——— No Matches Found ——— */}
{matchStatus === 'no_matches' && (
  <div className="no-matches-message">
    Sorry, we couldn’t find any investors that match your criteria at this time.<br/>
    Please hang tight and try again later.
  </div>
)}
      
      {/* show matches once fetched */}
       {matchStatus === 'matches_found' && matchedResults.length > 0 && (
        <div className="matched-results">
          <h3>Matched Investors</h3>
           {matchedResults.map((match, index) => (
            <div key={index} className="match-card">
               <p><b>Name:</b> {match.business_name || '–'}</p>
              <p><b>Location:</b> {match.location || 'Not Provided'}</p>
              <p>
                <b>Investment Target:</b> {`${match.investment_target_min} - ${match.investment_target_max}`}
              </p>
              <p>
                <b>Sectors of Interest:</b> {match.sectors_of_interest.join(", ")}
              </p>
              <p>
                <b>Investment Type:</b> {match.investment_types_investor.join(", ")}
              </p>
              <p><b>Match Score:</b> {match.match_score || 'Not Available'}</p>
              
            </div>
          ))}
          <button
            className="re-run-btn"
            onClick={handleRerunMatch}
            disabled={rerunCount >= 2}
          >
            Re-run Match {rerunCount >= 2 && '(Limit reached)'}
          </button>
          {rerunError && <div className="rerun-error">{rerunError}</div>}
        </div>
      )}

      {/* ---------------- Financials Confirmation Modal ---------------- */}
      {showFinancialModal && (
        <div className="modal-overlay">
          <div className="eligibility-modal">
            <p className="eligibility-text">{financialsMessage}</p>
            <div className="modal-buttons">
              <button className="confirm-btn" onClick={handleFinancialYes}>
                Yes, I have them
              </button>
              <button className="cancel-btn" onClick={handleFinancialNo}>
                No, I need help
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Enquiry Modal ---------------- */}
      {showEnquiryModal && (
        <div className="modal-overlay">
          <div className="eligibility-modal">
            <h3>Need Assistance?</h3>
            <p>
              It looks like you don’t have the required financial records.
              Please reach out to our Private Enterprise team for help.
              Make sure to use your company email when you write.
            </p>
            <div className="modal-buttons">
              <button className="confirm-btn" onClick={openEnquiryEmail}>
                Send Inquiry Email&nbsp;<FaEnvelope />
              </button>
              <button className="cancel-btn" onClick={() => setShowEnquiryModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Eligibility Modal ---------------- */}
      {showEligibilityModal && (
        <div className="modal-overlay">
          <div className="eligibility-modal">
            <p className="eligibility-text">{eligibilityMessage}</p>
            <div className="modal-buttons">
              <button className="confirm-btn" onClick={confirmEligibility}>
                Yes, proceed
              </button>
              <button className="cancel-btn" onClick={cancelEligibility}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchMakingPage;
